import { Component, Output, EventEmitter, inject, Input, OnChanges, OnInit, DestroyRef, computed, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemWalletTypeEnum, WalletTypeEnum } from '@olv/ui-shared/models/wallet.model';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { TextInputComponent } from '../text-input/text-input.component';
import { ValidationService } from '../../services/validation.service';
import { FormErrorService } from '../../services/form-error.service';
import { NetworkMetadataService } from '../../services/network-metadata.service';
import { TRON_NETWORK_TYPE } from '../../models/network-metadata.model';
import { coinIcon, FALLBACK_COIN_ICON } from '../../utils/asset-balance.utils';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-create-wallet-form',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, TextInputComponent, DropdownComponent, ButtonComponent],
  templateUrl: './create-wallet-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./create-wallet-form.component.scss'],
})
export class CreateWalletFormComponent implements OnInit, OnChanges {
  @Input() showSystemWalletType = false;
  @Input() submitting = false;
  @Output() cancelled = new EventEmitter<void>();
  @Output() formSubmit = new EventEmitter<any>();

  // Expose enums to the template
  WalletTypeEnum = WalletTypeEnum;

  selectedBlockchain: number | null = null;

  // Form options

  private readonly allWalletTypeOptions = [
    { value: WalletTypeEnum.Pooled, label: 'Pool Wallet' },
    { value: WalletTypeEnum.Dedicated, label: 'Dedicated Wallet' },
    { value: WalletTypeEnum.OnetimeUse, label: 'One-time Use Wallet' },
  ];
  walletTypeOptions = this.getWalletTypeOptions();
  systemWalletTypeOptions = [
    { value: SystemWalletTypeEnum.Normal, label: 'Normal Wallet' },
    { value: SystemWalletTypeEnum.Fees, label: 'Fees Wallet' },
    { value: SystemWalletTypeEnum.Staking, label: 'Staking Wallet' },
    { value: SystemWalletTypeEnum.Burning, label: 'Burning Wallet' },
    { value: SystemWalletTypeEnum.Funding, label: 'Funding Wallet' },
  ];

  protected readonly metadata = inject(NetworkMetadataService);
  private readonly tronOnly = signal(false);
  readonly blockchainOptions = computed(() =>
    this.metadata.walletCreationNetworks().map((network) => ({
      value: network.addressFamily,
      label: network.name,
      disabled: this.tronOnly() && network.networkType !== TRON_NETWORK_TYPE,
    })),
  );

  // Form group
  walletForm: FormGroup;

  private formErrorService = inject(FormErrorService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.metadata.load();
    this.walletForm = this.fb.group({
      walletType: [null, Validators.required],
      systemWalletType: [null],
      addressFamily: [null, Validators.required],
      name: ['', [Validators.required, ValidationService.textValidator(1, 60, '^[\\p{L}\\p{N} _-]*$')]],
    });
  }

  ngOnInit(): void {
    this.updateSystemWalletTypeControl();
    this.walletForm
      .get('walletType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateSystemWalletTypeControl();
      });
    this.walletForm
      .get('systemWalletType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateBlockchainRestriction());
  }

  ngOnChanges(): void {
    if (this.walletForm) {
      this.walletTypeOptions = this.getWalletTypeOptions();
      this.clearUnavailablePooledWalletType();
      this.updateSystemWalletTypeControl();
    }
  }

  get shouldShowSystemWalletType(): boolean {
    return this.showSystemWalletType && this.walletForm.get('walletType')?.value === WalletTypeEnum.Pooled;
  }

  getFieldError(controlName: string, fieldName: string): string {
    return this.formErrorService.getErrorMessage(this.walletForm.get(controlName), fieldName);
  }

  /**
   * Event emitter for when the user cancels wallet creation
   */
  onCancel(): void {
    this.cancelled.emit();
  }

  /**
   * Event emitter for when the user submits the wallet creation form
   */
  onSubmit(): void {
    if (this.submitting) {
      return;
    }

    const walletTypeControl = this.walletForm.get('walletType');
    if (!walletTypeControl || walletTypeControl.invalid) {
      walletTypeControl?.markAsTouched();
      return;
    }

    const addressFamilyControl = this.walletForm.get('addressFamily');
    if (!addressFamilyControl || addressFamilyControl.invalid) {
      addressFamilyControl?.markAsTouched();
      return;
    }

    const systemWalletTypeControl = this.walletForm.get('systemWalletType');
    if (this.shouldShowSystemWalletType && (!systemWalletTypeControl || systemWalletTypeControl.invalid)) {
      systemWalletTypeControl?.markAsTouched();
      return;
    }

    const nameControl = this.walletForm.get('name');
    const name = nameControl?.value?.trim();

    if (!name) {
      nameControl?.setErrors({ required: true });
      nameControl?.markAsTouched();
      return;
    }

    const formData = {
      walletType: walletTypeControl.value,
      addressFamily: addressFamilyControl.value,
      name,
      ...(this.shouldShowSystemWalletType ? { systemWalletType: systemWalletTypeControl?.value } : {}),
    };

    this.formSubmit.emit(formData);
  }
  getNetworkIcon(): string {
    const addressFamily = this.walletForm.get('addressFamily')?.value;
    const network = this.metadata.walletCreationNetworks().find((option) => option.addressFamily === addressFamily);
    return network ? coinIcon(network.iconKey) : FALLBACK_COIN_ICON;
  }
  get isButtonDisabled(): boolean {
    const walletType = this.walletForm.get('walletType');
    if (!walletType || walletType.invalid) {
      return true;
    }

    const addressFamily = this.walletForm.get('addressFamily');
    if (!addressFamily || addressFamily.invalid) {
      return true;
    }

    const systemWalletType = this.walletForm.get('systemWalletType');
    if (this.shouldShowSystemWalletType && (!systemWalletType || systemWalletType.invalid)) {
      return true;
    }

    const name = this.walletForm.get('name');
    if (!name || name.invalid || !name.value?.trim()) {
      return true;
    }

    return false;
  }

  get nextButtonState(): 'disabled' | 'default' {
    return this.isButtonDisabled || this.submitting ? 'disabled' : 'default';
  }

  /**
   * Sets backend validation errors on the form
   * @param errors - The errors object from the backend response
   */
  setBackendErrors(errors: { [key: string]: string[] }): void {
    if (errors['name']) {
      const nameControl = this.walletForm.get('name');
      nameControl?.setErrors({ backendError: errors['name'][0] });
      nameControl?.markAsTouched();
    }
  }

  private updateSystemWalletTypeControl(): void {
    const systemWalletTypeControl = this.walletForm.get('systemWalletType');
    if (!systemWalletTypeControl) {
      return;
    }

    if (this.shouldShowSystemWalletType) {
      systemWalletTypeControl.setValidators(Validators.required);
    } else {
      systemWalletTypeControl.clearValidators();
      systemWalletTypeControl.setValue(null, { emitEvent: false });
    }

    systemWalletTypeControl.updateValueAndValidity({ emitEvent: false });
    this.updateBlockchainRestriction();
  }

  private updateBlockchainRestriction(): void {
    this.tronOnly.set(this.walletForm.get('systemWalletType')?.value === SystemWalletTypeEnum.Staking);
    const addressFamilyControl = this.walletForm.get('addressFamily');
    if (this.blockchainOptions().some((option) => option.disabled && option.value === addressFamilyControl?.value)) {
      addressFamilyControl?.setValue(null);
    }
  }

  private getWalletTypeOptions() {
    return this.showSystemWalletType
      ? this.allWalletTypeOptions
      : this.allWalletTypeOptions.filter((option) => option.value !== WalletTypeEnum.Pooled);
  }

  private clearUnavailablePooledWalletType(): void {
    const walletTypeControl = this.walletForm.get('walletType');
    if (!this.showSystemWalletType && walletTypeControl?.value === WalletTypeEnum.Pooled) {
      walletTypeControl.setValue(null, { emitEvent: false });
    }
  }
}
